import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "../../supabase";
import { EVENT_TYPES } from '../../constants/eventTypes';
import { getCityFromZip } from '../../constants/zipData';
import { distanceMiles, isKnownZip } from '../../utils/distance';
import { fmtDate, fmtTime } from '../../utils/formatting';

// ─── Event Guest Dashboard ─────────────────────────────────────────────────────
function EventGoerDashboard({ profile, setProfile, authUser, manageToken, opps, acceptedBookings, setShowContactModal, setShowFeedbackModal }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name:profile.name, zip:profile.zip, radius:profile.radius, eventTypes:profile.event_types||[], frequency:profile.email_frequency });
  const [saving, setSaving] = useState(false);
  const [savedEvents, setSavedEvents] = useState(() => { try { return JSON.parse(localStorage.getItem('sjvm_saved_events')||'[]'); } catch { return []; } });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleType = (t) => setForm(f=>({...f, eventTypes: f.eventTypes.includes(t) ? f.eventTypes.filter(x=>x!==t) : [...f.eventTypes, t]}));
  const toggleSave = (id) => { setSavedEvents(s => { const next = s.includes(id) ? s.filter(x=>x!==id) : [...s,id]; localStorage.setItem('sjvm_saved_events',JSON.stringify(next)); return next; }); };
  const shareEvent = (opp) => { const url = window.location.origin; const text = `${opp.eventName} — ${fmtDate(opp.date)} in ${getCityFromZip(opp.zip)||'South Jersey'}`; if (navigator.share) navigator.share({title:opp.eventName,text,url}).catch(err=>console.error('Share failed:',err)); else { navigator.clipboard.writeText(`${text}\n${url}`).then(()=>alert('Event link copied!')).catch(err=>{console.error('Clipboard copy failed:',err);alert('Could not copy to clipboard.');}); } };

  // Browse mode: 'matching' (filtered by preferences) or 'all' (all events with filters)
  const [browseMode, setBrowseMode] = useState('matching');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterTicketed, setFilterTicketed] = useState('');
  const [filterZip, setFilterZip] = useState('');
  const [filterRadius, setFilterRadius] = useState(0);
  const vendorsByEvent = useMemo(() => {
    const byEvent = {};
    opps.forEach(o => { byEvent[o.id] = []; });
    (acceptedBookings || []).forEach(v => {
      const opp = opps.find(o => o.eventName === v.event_name && o.date === v.event_date);
      if (opp && !byEvent[opp.id]?.some(existing => existing.vendor_name === v.vendor_name)) {
        if (!byEvent[opp.id]) byEvent[opp.id] = [];
        byEvent[opp.id].push(v);
      }
    });
    return byEvent;
  }, [acceptedBookings, opps]);

  const isTokenMode = !!manageToken && !authUser;

  const handleSave = async () => {
    setSaving(true);
    if (isTokenMode) {
      try {
        const resp = await fetch('/api/update-event-goer-by-token', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ token: manageToken, action:'update', payload: { name:form.name, zip:form.zip, radius:form.radius, eventTypes:form.eventTypes, frequency:form.frequency } }),
        });
        if (!resp.ok) throw new Error('Update failed');
        const body = await resp.json().catch(()=>({}));
        if (body.profile && setProfile) setProfile(body.profile);
      } catch (err) {
        console.error('Token update failed:', err);
        alert('Failed to save. Please try again.');
        setSaving(false); return;
      }
    } else {
      const { error } = await supabase.from('event_goers').update({ name:form.name, zip:form.zip, radius:form.radius, event_types:form.eventTypes, email_frequency:form.frequency }).eq('id', profile.id);
      if (error) { console.error('Update failed:', error); alert('Failed to save. Please try again.'); setSaving(false); return; }
      if (setProfile) setProfile(p => ({ ...p, name:form.name, zip:form.zip, radius:form.radius, event_types:form.eventTypes, email_frequency:form.frequency }));
    }
    setSaving(false); setEditing(false);
  };

  const handleStopAlerts = async () => {
    if (!window.confirm('Stop receiving event alerts? You can always sign up again later.')) return;
    if (isTokenMode) {
      try {
        const resp = await fetch('/api/update-event-goer-by-token', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ token: manageToken, action:'stop' }),
        });
        if (!resp.ok) throw new Error('Stop failed');
      } catch (err) { console.error('Stop alerts failed:', err); alert('Failed to update. Please try again.'); return; }
    } else {
      await supabase.from('event_goers').update({ active:false, email_frequency:'none' }).eq('id', profile.id);
    }
    alert('Event alerts cancelled. You will no longer receive emails.');
    if (isTokenMode) { window.location.href = '/'; } else { window.location.reload(); }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Delete your account and all data? This cannot be undone.')) return;
    if (!window.confirm('Are you absolutely sure?')) return;
    await supabase.from('event_goers').delete().eq('id', profile.id).catch(err=>console.error('Delete event goer failed:',err));
    await supabase.auth.signOut();
    window.location.reload();
  };

  const guestZipOk = form.zip && form.zip.length === 5 && isKnownZip(form.zip);
  const todayStr = new Date().toISOString().split('T')[0];

  const [showPastEvents, setShowPastEvents] = useState(false);

  // All future guest-visible events, sorted nearest first
  const allFuture = opps
    .filter(o => o.date >= todayStr && o.shareWithEventGoers !== false)
    .sort((a,b) => a.date.localeCompare(b.date));

  // Past guest-visible events, sorted most recent first
  const pastEvents = opps
    .filter(o => o.date < todayStr && o.shareWithEventGoers !== false)
    .sort((a,b) => b.date.localeCompare(a.date));

  // Preference-matched events
  const matched = allFuture.filter(o => {
    if (form.eventTypes.length > 0 && !(o.eventTypes || [o.eventType]).some(t => form.eventTypes.includes(t))) return false;
    if (guestZipOk && form.radius) {
      const dist = distanceMiles(form.zip, o.zip);
      if (dist !== null && dist > form.radius) return false;
    }
    return true;
  });

  // Saved events
  const savedOpps = allFuture.filter(o => savedEvents.includes(o.id));

  // Browse events: saved, matched, or all, with optional date/type/zip filters
  const browseBase = browseMode === 'saved' ? savedOpps : browseMode === 'matching' ? matched : allFuture;
  const filterZipOk = filterZip.length === 5 && isKnownZip(filterZip);
  const browseFiltered = browseBase
    .filter(o => !filterDateFrom || o.date >= filterDateFrom)
    .filter(o => !filterDateTo || o.date <= filterDateTo)
    .filter(o => !filterType || (o.eventTypes || [o.eventType]).includes(filterType))
    .filter(o => !filterTicketed || (filterTicketed === 'yes' ? o.isTicketed : !o.isTicketed))
    .filter(o => {
      if (!filterZipOk || !filterRadius) return true;
      const dist = distanceMiles(filterZip, o.zip);
      return dist === null || dist <= filterRadius;
    });

  // Reusable event card renderer
  const renderEventCard = (o, opts = {}) => (
    <div key={o.id} style={{background:'#fff',border: opts.highlight ? '1px solid #ffd966' : '1px solid #e8ddd0',borderRadius:12,overflow:'hidden'}}>
      {o.photoUrl && (
        <div style={{width:'100%',height:180,overflow:'hidden'}}>
          <img src={o.photoUrl} alt={o.eventName} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
        </div>
      )}
      <div style={{background:'linear-gradient(135deg,#1a1410,#2d2118)',padding:'14px 20px'}}>
        <div style={{fontFamily:'Playfair Display,serif',fontSize:18,color:'#fff',lineHeight:1.3}}>{o.eventName}</div>
        {(o.orgName || o.contactName) && <div style={{fontSize:12,color:'#cdb98a',marginTop:2}}>Hosted by {o.orgName || o.contactName}</div>}
        <div style={{fontSize:11,color:'#a89a8a',letterSpacing:'1px',textTransform:'uppercase',marginTop:2}}>{(o.eventTypes || [o.eventType]).join(' · ')}</div>
      </div>
      <div style={{padding:'14px 20px'}}>
        <div style={{display:'flex',flexWrap:'wrap',gap:'8px 20px',marginBottom: (o.eventGoerNotes || o.eventLink) ? 12 : 0}}>
          <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Date </span><span style={{fontSize:13,fontWeight:500}}>{fmtDate(o.date)}</span></div>
          {(o.startTime || o.endTime) && <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Time </span><span style={{fontSize:13,fontWeight:500}}>{fmtTime(o.startTime)}{o.endTime ? ' – '+fmtTime(o.endTime) : ''}</span></div>}
          <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Location </span><span style={{fontSize:13,fontWeight:500}}>{getCityFromZip(o.zip) || 'Zip '+o.zip}</span></div>
          {o.isTicketed && <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Admission </span><span style={{fontSize:13,fontWeight:500}}>🎟️ {o.ticketPrice || 'Ticketed'}</span></div>}
          {!o.isTicketed && <div><span style={{fontSize:10,textTransform:'uppercase',letterSpacing:1,color:'#a89a8a',fontWeight:600}}>Admission </span><span style={{fontSize:13,fontWeight:500,color:'#1a6b3a'}}>Free</span></div>}
        </div>
        {o.eventGoerNotes && <p style={{fontSize:13,color:'#7a6a5a',lineHeight:1.5,margin:'0 0 10px',padding:'8px 12px',background:'#fdf9f5',borderRadius:6,borderLeft:'3px solid #e8c97a'}}>{o.eventGoerNotes}</p>}
        {o.eventLink && <div style={{marginBottom:4}}><a href={o.eventLink} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:'#1a4a6b',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:4}}>🔗 Event Page</a></div>}
      </div>
      {/* Vendors attending */}
      {(() => {
        const vendors = vendorsByEvent[o.id] || [];
        return (
          <div style={{borderTop:'1px solid #e8ddd0',padding:'12px 20px',background:'#fdf9f5'}}>
            <div style={{fontSize:11,fontWeight:700,color:'#1a1410',letterSpacing:0.5,marginBottom:6}}>Vendors Attending</div>
            {vendors.length === 0 ? (
              <div style={{color:'#7a6a5a',fontSize:12}}>No confirmed vendors yet. Check back closer to the event date!</div>
            ) : (
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {vendors.map((v,i) => (
                  <div key={i} style={{background:'#fff',border:'1px solid #e8ddd0',borderRadius:6,padding:'5px 10px',display:'flex',alignItems:'center',gap:6}}>
                    {v.vendor_emoji && <span style={{fontSize:14}}>{v.vendor_emoji}</span>}
                    <div>
                      <div style={{fontWeight:700,fontSize:11,color:'#1a1410'}}>{v.vendor_name}</div>
                      {v.vendor_category && <div style={{fontSize:9,color:'#7a6a5a'}}>{v.vendor_category}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
      <div style={{borderTop:'1px solid #e8ddd0',padding:'8px 20px',display:'flex',gap:8}}>
        <button onClick={()=>toggleSave(o.id)} style={{flex:1,background:savedEvents.includes(o.id)?'#fdf4dc':'#f5f0ea',color:savedEvents.includes(o.id)?'#7a5a10':'#7a6a5a',border:'1px solid #e8ddd0',borderRadius:6,padding:'7px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>{savedEvents.includes(o.id)?'♥ Saved':'♡ Save'}</button>
        <button onClick={()=>shareEvent(o)} style={{flex:1,background:'#f5f0ea',color:'#7a6a5a',border:'1px solid #e8ddd0',borderRadius:6,padding:'7px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>↗ Share</button>
      </div>
    </div>
  );

  return (
    <div className="section" style={{maxWidth:900}}>
      <div className="section-title" style={{color:'#e8c97a'}}>Event Guest Dashboard</div>
      <p className="section-sub" style={{color:'#b8a888'}}>Welcome back, {profile.name}{isTokenMode ? ' — managing via email link' : ''}</p>

      {/* ── Preferences ── */}
      <div style={{background:'#fff',border:'1px solid #e8ddd0',borderRadius:12,padding:24,marginBottom:24}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h3 style={{fontFamily:'Playfair Display,serif',fontSize:20,margin:0}}>My Preferences</h3>
          <button onClick={()=>setEditing(!editing)} style={{background:'none',border:'1px solid #c8a850',color:'#c8a850',borderRadius:6,padding:'6px 16px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>{editing ? 'Cancel' : 'Edit'}</button>
        </div>
        {editing ? (
          <>
            <div className="form-grid" style={{gap:12,marginBottom:14}}>
              <div className="form-group"><label>Name</label><input value={form.name} onChange={e=>set('name',e.target.value)} /></div>
              <div className="form-group"><label>Zip Code</label><input value={form.zip} onChange={e=>set('zip',e.target.value.replace(/\D/g,'').slice(0,5))} maxLength={5} /></div>
              <div className="form-group"><label>Travel Radius</label><select value={form.radius} onChange={e=>set('radius',+e.target.value)}>{[5,10,15,20,30,50].map(r=><option key={r} value={r}>{r} miles</option>)}</select></div>
              <div className="form-group"><label>Email Frequency</label><select value={form.frequency} onChange={e=>set('frequency',e.target.value)}><option value="weekly">Weekly</option><option value="biweekly">Every 2 Weeks</option></select></div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:13,fontWeight:600,display:'block',marginBottom:6}}>Event Types</label>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {EVENT_TYPES.filter(t=>t!=='Other'&&t!=='Private Party').map(t=>(
                  <button key={t} onClick={()=>toggleType(t)} style={{padding:'5px 12px',borderRadius:16,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif',border:'1.5px solid',background:form.eventTypes.includes(t)?'#c8a850':'#fff',color:form.eventTypes.includes(t)?'#1a1410':'#7a6a5a',borderColor:form.eventTypes.includes(t)?'#c8a850':'#e8ddd0'}}>{t}</button>
                ))}
              </div>
            </div>
            <button onClick={handleSave} disabled={saving} style={{background:'#c8a850',color:'#1a1410',border:'none',borderRadius:8,padding:'10px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>{saving ? 'Saving...' : 'Save Preferences'}</button>
          </>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 24px'}}>
            <div><span style={{fontSize:12,color:'#a89a8a',fontWeight:600}}>Location:</span> Zip {profile.zip} ({profile.radius}mi radius)</div>
            <div><span style={{fontSize:12,color:'#a89a8a',fontWeight:600}}>Email:</span> {profile.email_frequency === 'weekly' ? 'Weekly' : profile.email_frequency === 'biweekly' ? 'Biweekly' : 'No alerts'}</div>
            <div style={{gridColumn:'1/-1'}}><span style={{fontSize:12,color:'#a89a8a',fontWeight:600}}>Event Types:</span> {(profile.event_types||[]).join(', ') || 'All types'}</div>
          </div>
        )}
      </div>

      {/* ── Browse Events ── */}
      <div style={{marginBottom:24}}>
        <h3 style={{fontFamily:'Playfair Display,serif',fontSize:20,marginBottom:12}}>Browse Events</h3>

        {/* Mode toggle */}
        <div style={{display:'flex',gap:0,marginBottom:16,borderRadius:8,overflow:'hidden',border:'2px solid #c8a850'}}>
          <button onClick={()=>setBrowseMode('saved')} style={{flex:1,padding:'10px 12px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',border:'none',background:browseMode==='saved'?'#c8a850':'#fff',color:browseMode==='saved'?'#1a1410':'#7a6a5a'}}>
            My Saved ({savedOpps.length})
          </button>
          <button onClick={()=>setBrowseMode('matching')} style={{flex:1,padding:'10px 12px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',border:'none',borderLeft:'1px solid #e8ddd0',background:browseMode==='matching'?'#c8a850':'#fff',color:browseMode==='matching'?'#1a1410':'#7a6a5a'}}>
            My Matches ({matched.length})
          </button>
          <button onClick={()=>setBrowseMode('all')} style={{flex:1,padding:'10px 12px',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'DM Sans,sans-serif',border:'none',borderLeft:'1px solid #e8ddd0',background:browseMode==='all'?'#c8a850':'#fff',color:browseMode==='all'?'#1a1410':'#7a6a5a'}}>
            All Events ({allFuture.length})
          </button>
        </div>

        {/* Filters */}
        <div className="match-filters" style={{display:'flex',flexWrap:'wrap',gap:12,marginBottom:16}}>
          <div className="match-filter-group" style={{minWidth:110}}>
            <label style={{fontSize:11,fontWeight:600,color:'#a89a8a',display:'block',marginBottom:4}}>Zip Code</label>
            <input placeholder="e.g. 08033" value={filterZip} maxLength={5} onChange={e=>setFilterZip(e.target.value.replace(/\D/g,'').slice(0,5))} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:'1px solid #e8ddd0',fontSize:13,fontFamily:'DM Sans,sans-serif'}} />
            {filterZip.length===5 && <div style={{fontSize:10,marginTop:2,color:filterZipOk?'#1a6b3a':'#c0392b'}}>{filterZipOk ? (filterRadius ? `Within ${filterRadius} mi` : 'Sorted by distance') : 'Zip not recognized'}</div>}
          </div>
          {filterZipOk && (
            <div className="match-filter-group" style={{minWidth:100}}>
              <label style={{fontSize:11,fontWeight:600,color:'#a89a8a',display:'block',marginBottom:4}}>Radius</label>
              <select value={filterRadius} onChange={e=>setFilterRadius(+e.target.value)} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:'1px solid #e8ddd0',fontSize:13,fontFamily:'DM Sans,sans-serif'}}>
                <option value={0}>Any distance</option>
                {[5,10,15,20,30,50].map(r=><option key={r} value={r}>{r} miles</option>)}
              </select>
            </div>
          )}
          <div className="match-filter-group" style={{minWidth:130}}>
            <label style={{fontSize:11,fontWeight:600,color:'#a89a8a',display:'block',marginBottom:4}}>From Date</label>
            <input type="date" value={filterDateFrom} min={todayStr} onChange={e=>setFilterDateFrom(e.target.value)} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:'1px solid #e8ddd0',fontSize:13,fontFamily:'DM Sans,sans-serif'}} />
          </div>
          <div className="match-filter-group" style={{minWidth:130}}>
            <label style={{fontSize:11,fontWeight:600,color:'#a89a8a',display:'block',marginBottom:4}}>To Date</label>
            <input type="date" value={filterDateTo} min={filterDateFrom||todayStr} onChange={e=>setFilterDateTo(e.target.value)} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:'1px solid #e8ddd0',fontSize:13,fontFamily:'DM Sans,sans-serif'}} />
          </div>
          <div className="match-filter-group" style={{minWidth:140}}>
            <label style={{fontSize:11,fontWeight:600,color:'#a89a8a',display:'block',marginBottom:4}}>Event Type</label>
            <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:'1px solid #e8ddd0',fontSize:13,fontFamily:'DM Sans,sans-serif'}}>
              <option value="">All Types</option>
              {EVENT_TYPES.filter(t=>t!=='Other'&&t!=='Private Party').map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="match-filter-group" style={{minWidth:120}}>
            <label style={{fontSize:11,fontWeight:600,color:'#a89a8a',display:'block',marginBottom:4}}>Admission</label>
            <select value={filterTicketed} onChange={e=>setFilterTicketed(e.target.value)} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:'1px solid #e8ddd0',fontSize:13,fontFamily:'DM Sans,sans-serif'}}>
              <option value="">All Events</option>
              <option value="yes">Ticketed Only</option>
              <option value="no">Free Only</option>
            </select>
          </div>
          {(filterDateFrom || filterDateTo || filterType || filterTicketed || filterZip || filterRadius) && (
            <div style={{display:'flex',alignItems:'flex-end'}}>
              <button onClick={()=>{setFilterDateFrom('');setFilterDateTo('');setFilterType('');setFilterTicketed('');setFilterZip('');setFilterRadius(0);}} style={{background:'none',border:'1px solid #e8ddd0',color:'#7a6a5a',borderRadius:6,padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Clear Filters</button>
            </div>
          )}
        </div>

        {/* Results */}
        <div style={{fontSize:13,color:'#7a6a5a',marginBottom:12}}>
          <strong>{browseFiltered.length}</strong> event{browseFiltered.length !== 1 ? 's' : ''}{browseMode === 'saved' ? ' saved' : browseMode === 'matching' ? ' matching your preferences' : ''}
          {(filterDateFrom || filterDateTo) && <span> · {filterDateFrom && !filterDateTo ? `from ${fmtDate(filterDateFrom)}` : !filterDateFrom && filterDateTo ? `through ${fmtDate(filterDateTo)}` : `${fmtDate(filterDateFrom)} – ${fmtDate(filterDateTo)}`}</span>}
        </div>

        {browseFiltered.length === 0 ? (
          <div className="empty-state"><div className="big">{browseMode === 'saved' ? '♡' : '📅'}</div><p>{browseMode === 'saved' ? 'No saved events yet. Switch to "My Matches" or "All Events" and tap the ♡ Save button on any event.' : browseMode === 'matching' ? 'No events match your preferences and filters. Try "All Events" or adjust your filters.' : 'No events match your filters. Try broadening your date range.'}</p></div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:16}}>
            {browseFiltered.map(o => renderEventCard(o, browseMode === 'saved' ? { highlight: true } : {}))}
          </div>
        )}
      </div>

      {/* ── Past Events ── */}
      {pastEvents.length > 0 && (
        <div style={{marginBottom:24}}>
          <button onClick={()=>setShowPastEvents(!showPastEvents)} style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex',alignItems:'center',gap:8,marginBottom:showPastEvents?12:0}}>
            <h3 style={{fontFamily:'Playfair Display,serif',fontSize:20,margin:0,color:'#1a1410'}}>Past Events ({pastEvents.length})</h3>
            <span style={{fontSize:16,color:'#a89a8a'}}>{showPastEvents ? '▲' : '▼'}</span>
          </button>
          {showPastEvents && (
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              {pastEvents.map(o => renderEventCard(o))}
            </div>
          )}
        </div>
      )}

      <div style={{background:'#f5f0ea',border:'1px solid #e8ddd0',borderRadius:10,padding:'16px 20px',marginTop:24,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12}}>
        <div style={{fontSize:13,color:'#7a6a5a'}}>Questions or feedback?</div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={()=>setShowFeedbackModal(true)} style={{background:'#fff',color:'#1a1410',border:'1px solid #e8ddd0',borderRadius:6,padding:'8px 20px',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Give Feedback</button>
          <button onClick={()=>setShowContactModal(true)} style={{background:'#1a1410',color:'#e8c97a',border:'none',borderRadius:6,padding:'8px 20px',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Contact Us</button>
        </div>
      </div>

      {/* Account management */}
      <div style={{borderTop:'1px solid #e8ddd0',paddingTop:20,marginBottom:24}}>
        <div style={{fontSize:12,fontWeight:700,color:'#a89a8a',letterSpacing:1,textTransform:'uppercase',marginBottom:12}}>Account Management</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button onClick={handleStopAlerts} style={{background:'#f5f0ea',color:'#7a6a5a',border:'1px solid #e0d5c5',borderRadius:6,padding:'8px 16px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Stop Event Alerts</button>
          {!isTokenMode && (
            <button onClick={handleDeleteAccount} style={{background:'none',border:'1px solid #e0d5c5',color:'#8b1a1a',borderRadius:6,padding:'8px 16px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'DM Sans,sans-serif'}}>Delete Entire Account</button>
          )}
        </div>
        {isTokenMode && (
          <div style={{fontSize:11,color:'#a89a8a',marginTop:10,lineHeight:1.5}}>
            You're managing your event guest profile via the link in your welcome email. To delete your account or access more features, <button onClick={()=>{window.location.href='/';}} style={{background:'none',border:'none',color:'#c8a850',textDecoration:'underline',padding:0,cursor:'pointer',fontFamily:'inherit',fontSize:11}}>create an account</button> with the same email address.
          </div>
        )}
      </div>
    </div>
  );
}

export default EventGoerDashboard;
